import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// On a 401, transparently try one cookie-based token refresh, then replay the
// original request. A single shared in-flight refresh avoids a stampede when
// several requests 401 at once. Auth endpoints are excluded to prevent loops.
let refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    const isAuthRoute = /\/auth\/(refresh|login|logout|register)/.test(url);
    if (status === 401 && original && !original._retry && !isAuthRoute) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        return api(original);
      } catch (e) {
        return Promise.reject(error);
      } finally {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  // Structured quota 402: {error, message, dimension, used, limit, upgrade_to}.
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * quotaError — extract the structured quota/credit 402 payload, or null.
 * Lets callers open the right paywall: e.g. const q = quotaError(err); if (q)
 * openPaywall(q.dimension, q.upgrade_to).
 */
export function quotaError(err) {
  if (err?.response?.status !== 402) return null;
  const d = err?.response?.data?.detail;
  if (
    d &&
    typeof d === "object" &&
    (d.error === "quota_exceeded" || d.error === "ai_requires_paid_plan")
  ) {
    return {
      error: d.error,
      dimension: d.dimension || null,
      used: d.used ?? null,
      limit: d.limit ?? null,
      plan: d.plan || null,
      upgradeTo: d.upgrade_to || "pro",
      message: d.message || "Plan limit reached.",
    };
  }
  return null;
}
